import type { PagedEnvelope } from '@bookora/ui';

import {
  patientPaymentsCreate,
  patientPaymentsList,
} from '@/api/generated/accounting-patient-payments/accounting-patient-payments';
import type {
  BlacklistPatientDto,
  CompleteReminderDto,
  CreateDataExportDto,
  CreateErasureRequestDto,
  CreatePatientDto,
  CreatePatientNoteDto,
  CreatePatientPaymentDto,
  DataExportDownloadResponse,
  DataExportResponse,
  DuplicateMatchResponse,
  ErasurePendingCountResponse,
  ErasureRequestResponse,
  MergePatientsDto,
  MergeResultResponse,
  PackageResponse,
  PatientDetailResponse,
  PatientFacetValueResponse,
  PatientFacetsResponse,
  PatientNoteResponse,
  PatientNotesListParams,
  PatientPaymentResponse,
  PatientRegistrationLimitResponse,
  PatientResponse,
  PatientSummaryResponse,
  PatientsListParams,
  ReminderResponse,
  SessionResponse,
  StaffResponse,
  UpdatePatientDto,
  UpdatePatientNoteDto,
} from '@/api/generated/model';
import { packagesList } from '@/api/generated/packages/packages';
import {
  patientErasureApprove,
  patientErasureList,
  patientErasurePendingCount,
  patientErasureReject,
  patientErasureRequest,
  patientExportsDownload,
  patientExportsFindOne,
  patientExportsList,
  patientExportsRequest,
} from '@/api/generated/patient-privacy/patient-privacy';
import {
  patientNotesCreate,
  patientNotesList,
  patientNotesRemove,
  patientNotesUpdate,
  patientsCreate,
  patientsDuplicateCheck,
  patientsFacets,
  patientsFindOne,
  patientsList,
  patientsMerge,
  patientsOverview,
  patientsRecycleBin,
  patientsRegistrationLimit,
  patientsRemove,
  patientsRestore,
  patientsSetBlacklist,
  patientsTags,
  patientsUpdate,
} from '@/api/generated/patients/patients';
import { remindersComplete } from '@/api/generated/reminders/reminders';
import { sessionsList } from '@/api/generated/treatment-sessions/treatment-sessions';
import { staffList } from '@/api/generated/staff/staff';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE PATIENT MODULE'S ONLY DOOR TO THE API
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Every call below is an orval-generated function from `src/api/generated/`,
 * which means the URL, the query-parameter names and the request bodies come
 * from the backend's own OpenAPI document rather than from a hand-typed string
 * this feature would have to keep in sync. Nothing here builds a request.
 *
 * | Screen                | Endpoint                          | Permission              |
 * |-----------------------|-----------------------------------|-------------------------|
 * | فهرست بیماران          | GET    /patients                  | patient.view            |
 * | «فقط بدهکارها»         | GET    /patients?hasOutstanding…  | + finance.view_revenue  |
 * | شهرها و برچسب‌ها        | GET    /patients/facets · /tags   | patient.view            |
 * | ظرفیت ثبت بیمار         | GET    …/registration-limit       | patient.create          |
 * | سطل بازیافت            | GET    /patients/recycle-bin      | patient.view + .delete  |
 * | بررسی تکراری           | GET    /patients/duplicate-check  | patient.view            |
 * | ثبت بیمار              | POST   /patients                  | patient.create          |
 * | پروندهٔ کامل            | GET    /patients/:id/overview     | patient.view            |
 * | ویرایش                 | PATCH  /patients/:id              | patient.edit            |
 * | محدود کردن             | POST   /patients/:id/blacklist    | patient.edit            |
 * | حذف (سطل بازیافت)      | DELETE /patients/:id              | patient.delete          |
 * | بازگرداندن             | POST   /patients/:id/restore      | patient.delete          |
 * | ادغام پروندهٔ تکراری    | POST   /patients/:id/merge        | patient.edit + .delete  |
 * | یادداشت‌ها              | …/notes  GET/POST/PATCH/DELETE    | patient.view / .edit    |
 * | دریافت‌های ثبت‌شده       | GET    /patient-payments          | finance.view_revenue    |
 * | ثبت دریافت             | POST   /patient-payments          | package.sell            |
 * | نسخهٔ کامل پرونده       | /patient-exports  GET/POST        | patient.export          |
 * | درخواست حذف دائمی       | /patient-erasure-requests         | patient.delete          |
 * | شمار درخواست‌های منتظر   | GET …/pending-count               | patient.delete          |
 * | پیگیری انجام شد        | POST   /reminders/:id/complete    | session.edit            |
 * | بسته‌های خدمات قابل فروش      | GET    /packages                  | package.view            |
 *
 * ── DOCTOR SCOPING AND THE `total` FIELD ────────────────────────────────────
 *
 * `GET /patients` narrows to the caller's own assigned patients when they lack
 * `patient.view_all`, and the `total` in the envelope counts only the rows they
 * were allowed to see. So the table's «۱۲ مورد» is honest for every role and the
 * panel never has to subtract one number from another it cannot see.
 *
 * ── WHY THESE THIN WRAPPERS EXIST AT ALL ────────────────────────────────────
 *
 * One type-level correction, made once here so no screen re-derives it. The
 * OpenAPI document declares a list response as `{ data: { items, page, … } }`,
 * so orval types the paged calls as `…List200`. But `apiClient`
 * (`src/api/mutator.ts`) strips exactly one `data` level before the promise
 * resolves — every caller in the panel receives the inner object. The generated
 * type is therefore one level deeper than the runtime value, and `paged()`
 * below is the single place that is reconciled.
 *
 * It is a cast rather than a runtime parse on purpose: the API's real payload is
 * not in doubt, only the document's description of the envelope. Everything
 * else — including every nullable scalar — is the generated type, unmodified.
 *
 * ── DATES ARE STRINGS ───────────────────────────────────────────────────────
 *
 * Every timestamp on the wire is an ISO-8601 `string`, never a `Date`. The
 * Jalali components take a string directly, so nothing in this module parses
 * one except the edit form, which needs a `Dayjs` for the picker.
 */

function paged<T>(value: unknown): PagedEnvelope<T> {
  return value as PagedEnvelope<T>;
}

export function listPatients(
  params: PatientsListParams,
  signal?: AbortSignal,
): Promise<PagedEnvelope<PatientSummaryResponse>> {
  return patientsList(params, signal).then(paged<PatientSummaryResponse>);
}

export function listDeletedPatients(
  params: PatientsListParams,
  signal?: AbortSignal,
): Promise<PagedEnvelope<PatientSummaryResponse>> {
  return patientsRecycleBin(params, signal).then(paged<PatientSummaryResponse>);
}

export function getPatient(id: string, signal?: AbortSignal): Promise<PatientResponse> {
  return patientsFindOne(id, signal);
}

export function getPatientOverview(
  id: string,
  signal?: AbortSignal,
): Promise<PatientDetailResponse> {
  return patientsOverview(id, signal);
}

export function createPatient(dto: CreatePatientDto): Promise<PatientResponse> {
  return patientsCreate(dto);
}

export function updatePatient(id: string, dto: UpdatePatientDto): Promise<PatientResponse> {
  return patientsUpdate(id, dto);
}

/** Soft delete — the row moves to the recycle bin, nothing is destroyed. */
export function deletePatient(id: string): Promise<void> {
  return patientsRemove(id);
}

export function restorePatient(id: string): Promise<PatientResponse> {
  return patientsRestore(id);
}

export function setPatientBlacklist(
  id: string,
  dto: BlacklistPatientDto,
): Promise<PatientResponse> {
  return patientsSetBlacklist(id, dto);
}

/**
 * The cities and tags actually in use, each with how many files carry it —
 * what the list's «شهر» and «برچسب» pickers offer before anyone types. Counted
 * over the files this caller may see (doctor-scoped, like the list itself), so
 * a count never hints at patients outside their scope.
 */
export function getPatientFacets(signal?: AbortSignal): Promise<PatientFacetsResponse> {
  return patientsFacets(signal);
}

/** The tag half of the facets, for the form's tag suggestions. */
export function listPatientTags(signal?: AbortSignal): Promise<PatientFacetValueResponse[]> {
  return patientsTags(signal);
}

/**
 * How many more files the clinic's plan admits. `limit`/`remaining` are `null`
 * for an unlimited plan. Read BEFORE the form is filled, so a full plan is said
 * at «ثبت بیمار جدید» rather than as a 402 after the whole file was typed.
 */
export function getRegistrationLimit(
  signal?: AbortSignal,
): Promise<PatientRegistrationLimitResponse> {
  return patientsRegistrationLimit(signal);
}

/**
 * Pre-flight duplicate check for the create form.
 *
 * The banner it produces links to the existing file (or restores it from the
 * recycle bin). A duplicate that slipped through anyway is fixed after the
 * fact by `mergePatient` below, from the surviving file's «بیشتر» menu.
 *
 * Matching runs against the HMAC blind indexes, so only an EXACT phone or
 * national ID matches — there is no fuzzy search over an encrypted column, and
 * the form must not pretend otherwise.
 */
export function checkDuplicate(
  params: { phone?: string; nationalId?: string },
  signal?: AbortSignal,
): Promise<DuplicateMatchResponse> {
  return patientsDuplicateCheck(params, signal);
}

/**
 * Folds `sourcePatientId` into `targetId`: every session, appointment, photo,
 * consent and payment moves, and the source file goes to the recycle bin. The
 * server re-checks that the caller may see BOTH files.
 */
export function mergePatient(
  targetId: string,
  dto: MergePatientsDto,
): Promise<MergeResultResponse> {
  return patientsMerge(targetId, dto);
}

/* -------------------------------------------------------------------------- */
/* Privacy: the full data package, and right-to-erasure requests              */
/* -------------------------------------------------------------------------- */

export function requestPatientExport(dto: CreateDataExportDto): Promise<DataExportResponse> {
  return patientExportsRequest(dto);
}

export function listPatientExports(
  patientId: string,
  signal?: AbortSignal,
): Promise<PagedEnvelope<DataExportResponse>> {
  return patientExportsList({ patientId, page: 1, pageSize: 5 }, signal).then(
    paged<DataExportResponse>,
  );
}

export function getPatientExport(id: string, signal?: AbortSignal): Promise<DataExportResponse> {
  return patientExportsFindOne(id, signal);
}

/** A signed URL that lives for five minutes — fetched at the moment of the click. */
export function getPatientExportDownload(id: string): Promise<DataExportDownloadResponse> {
  return patientExportsDownload(id);
}

export function requestErasure(dto: CreateErasureRequestDto): Promise<ErasureRequestResponse> {
  return patientErasureRequest(dto);
}

export function listErasureRequests(
  params: { page: number; pageSize: number },
  signal?: AbortSignal,
): Promise<PagedEnvelope<ErasureRequestResponse>> {
  return patientErasureList(params, signal).then(paged<ErasureRequestResponse>);
}

/** How many requests wait for a decision — the recycle-bin tab's badge. */
export function getErasurePendingCount(signal?: AbortSignal): Promise<ErasurePendingCountResponse> {
  return patientErasurePendingCount(signal);
}

export function approveErasure(id: string): Promise<ErasureRequestResponse> {
  return patientErasureApprove(id);
}

export function rejectErasure(
  id: string,
  rejectionReason: string,
): Promise<ErasureRequestResponse> {
  return patientErasureReject(id, { rejectionReason });
}

/* -------------------------------------------------------------------------- */
/* Actions the patient file starts in place                                   */
/* -------------------------------------------------------------------------- */

/** «ثبت دریافت» from the file — the same ledger entry the accounting tab writes. */
export function recordPatientPayment(dto: CreatePatientPaymentDto): Promise<unknown> {
  return patientPaymentsCreate(dto);
}

/**
 * The patient's sessions, for «بابت جلسه» on a receipt. `balanceRials` is the
 * server's own figure — the panel does not subtract `paid` from `total`.
 *
 * The API's page ceiling (100), newest first: an unpaid session older than the
 * hundredth is rare enough that the receipt can still be recorded against the
 * patient alone, and 50 cut off a long-running laser course.
 */
export function listPatientSessions(
  patientId: string,
  signal?: AbortSignal,
): Promise<SessionResponse[]> {
  return sessionsList({ patientId, pageSize: 100 }, signal).then((page) => page.items);
}

/** Closes a follow-up: a human did it, which is different from «the SMS went out». */
export function completeReminder(id: string, dto: CompleteReminderDto): Promise<ReminderResponse> {
  return remindersComplete(id, dto);
}

/** The clinic's active offers, for «فروش بسته خدمات» from the file. */
export function listSellablePackages(signal?: AbortSignal): Promise<PackageResponse[]> {
  return packagesList({ isActive: true, pageSize: 100 }, signal).then((page) => page.items);
}

/* -------------------------------------------------------------------------- */
/* Notes                                                                      */
/* -------------------------------------------------------------------------- */

export function listPatientNotes(
  patientId: string,
  params: PatientNotesListParams,
  signal?: AbortSignal,
): Promise<PagedEnvelope<PatientNoteResponse>> {
  return patientNotesList(patientId, params, signal).then(paged<PatientNoteResponse>);
}

export function createPatientNote(
  patientId: string,
  dto: CreatePatientNoteDto,
): Promise<PatientNoteResponse> {
  return patientNotesCreate(patientId, dto);
}

export function updatePatientNote(
  patientId: string,
  noteId: string,
  dto: UpdatePatientNoteDto,
): Promise<PatientNoteResponse> {
  return patientNotesUpdate(patientId, noteId, dto);
}

/** Soft delete, like everything else in this product. */
export function deletePatientNote(patientId: string, noteId: string): Promise<void> {
  return patientNotesRemove(patientId, noteId);
}

/* -------------------------------------------------------------------------- */
/* Recorded payments                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The patient's recorded receipts and refunds.
 *
 * «Recorded, not processed» — there is no patient payment gateway in this
 * product, so every row is something a human at the desk entered. Gated on
 * `finance.view_revenue`, the same key that decides whether the patient's money
 * figures come back at all.
 */
export function listPatientPayments(
  patientId: string,
  pageSize: number,
  signal?: AbortSignal,
): Promise<PagedEnvelope<PatientPaymentResponse>> {
  return patientPaymentsList({ patientId, page: 1, pageSize }, signal).then(
    paged<PatientPaymentResponse>,
  );
}

/* -------------------------------------------------------------------------- */
/* Doctors, for the «پزشک معالج» filter and field                              */
/* -------------------------------------------------------------------------- */

/**
 * The clinic's doctors. Needs `staff.view`, which is why every caller of this is
 * gated on that key and not on a patient key.
 */
export function listDoctors(signal?: AbortSignal): Promise<StaffResponse[]> {
  return staffList({ isDoctor: true, isActive: true, pageSize: 100 }, signal).then(
    (page) => page.items,
  );
}
